"use client"

import { CtaButton } from "@/component/cta-button";
import { Card } from "@/component/override/card.c";
import { Table } from "@/component/override/table.c";
import { routes } from "@/config/routes";
import { useLanguage } from "@/context/language-context";
import { useTranslations } from "@/hooks/use-translations";
import { getLocalizedPath } from "@/lib/routes";
import { Config } from "@/shared/config";
import { CardBody, CardHeader, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@heroui/react";
import { IconCheck } from "@tabler/icons-react";
import Link from "next/link";
import Script from "next/script";

export default function CompareOpificio42() {
    const t = useTranslations();
    const { language } = useLanguage();

    if (!t) return null;

    return (
        <>
            <link rel="canonical" href={`https://${Config.domainName}/alternative/coworking-varese-next`} />

            <Script
                id="comparison-schema"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Article",
                        "headline": "Confronto tra VareseLab e Opificio42 Coworking a Varese",
                        "description": "Analisi dettagliata delle differenze tra VareseLab e Opificio42 Coworking per aiutarti a scegliere il coworking ideale a Varese.",
                        "author": {
                            "@type": "Organization",
                            "name": "VareseLab"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "VareseLab"
                        },
                        "url": `https://${Config.domainName}/alternative/coworking-varese-next`,
                    }),
                }}
            />

            <div className="max-w-screen-desktop mx-auto w-full flex flex-col gap-12 desktop:gap-16 px-8 py-16 min-h-screen">

                {/* Hero Section */}
                <div className="text-center space-y-6">
                    <h1 className="font-h1 text-4xl desktop:text-5xl font-bold">
                        VareseLab vs Opificio42 Coworking
                    </h1>
                    <p className="text-lg text-default-600 max-w-3xl mx-auto">
                        Confronta i due principali coworking di Varese per trovare l'ambiente di lavoro perfetto per te
                    </p>
                </div>

                {/* Comparison Table */}
                <Table className="w-full">
                    <TableHeader>
                        <TableColumn className="bg-default-50 font-semibold">{t.comparison.table.features}</TableColumn>
                        <TableColumn className="bg-primary-50 font-semibold text-center text-primary-700">VareseLab</TableColumn>
                        <TableColumn className="bg-default-50 font-semibold text-center">Opificio42</TableColumn>
                    </TableHeader>
                    <TableBody>
                        <TableRow className="even:bg-default-100">
                            <TableCell className="font-medium text-foreground">{t.comparison.table.location}</TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div>{t.comparison.table.locationVareselab}</div>
                                    <div className="text-sm text-default-500">{t.comparison.table.locationVareselabDetail}</div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div>{t.comparison.table.locationOpificio42}</div>
                                    <div className="text-sm text-default-500">{t.comparison.table.locationOpificio42Detail}</div>
                                </div>
                            </TableCell>
                        </TableRow>

                        <TableRow className="even:bg-default-100">
                            <TableCell className="font-medium text-foreground">{t.comparison.table.pricing}</TableCell>
                            <TableCell>
                                <div className="space-y-2">
                                    <div className="">{t.comparison.table.pricingVareselab}</div>
                                    <div className="text-default-600">{t.comparison.table.pricingVareselabStandard}</div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <span className="text-default-500">{t.comparison.table.pricingOpificio42}</span>
                            </TableCell>
                        </TableRow>

                        <TableRow className="even:bg-default-100">
                            <TableCell className="font-medium text-foreground">{t.comparison.table.hours}</TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div>{t.comparison.table.hoursVareselab}</div>
                                    <div className="text-sm">{t.comparison.table.hoursVareselabDetail}</div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div>{t.comparison.table.hoursOpificio42}</div>
                                    <div className="text-sm">{t.comparison.table.hoursOpificio42Detail}</div>
                                </div>
                            </TableCell>
                        </TableRow>

                        <TableRow className="even:bg-default-100">
                            <TableCell className="font-medium text-foreground">{t.comparison.table.community}</TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <span>{t.comparison.table.communityNetworking}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span>{t.comparison.table.communityActive}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span>{t.comparison.table.communityFocus}</span>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div>{t.comparison.table.communityOpificio42}</div>
                                    <div className="text-sm text-default-500">{t.comparison.table.communityOpificio42Detail}</div>
                                </div>
                            </TableCell>
                        </TableRow>

                        <TableRow className="even:bg-default-100">
                            <TableCell className="font-medium text-foreground">{t.comparison.table.services}</TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <span>{t.comparison.table.servicesInternet}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span>{t.comparison.table.servicesBooking}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span>{t.comparison.table.servicesCoffee}</span>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div>{t.comparison.table.servicesOpificio42Meeting}</div>
                                    <div>{t.comparison.table.servicesOpificio42Studio}</div>
                                    <div>{t.comparison.table.servicesOpificio42Garden}</div>
                                    <div>{t.comparison.table.servicesOpificio42Break}</div>
                                </div>
                            </TableCell>
                        </TableRow>

                        <TableRow className="even:bg-default-100">
                            <TableCell className="font-medium text-foreground">{t.comparison.table.target}</TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div>{t.comparison.table.targetFreelancers}</div>
                                    <div>{t.comparison.table.targetRemote}</div>
                                    <div>{t.comparison.table.targetProfessionals}</div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div>{t.comparison.table.targetOpificio42Communication}</div>
                                    <div>{t.comparison.table.targetOpificio42Outsourcing}</div>
                                    <div>{t.comparison.table.targetOpificio42Creative}</div>
                                </div>
                            </TableCell>
                        </TableRow>

                        <TableRow className="even:bg-default-100">
                            <TableCell className="font-medium text-foreground">{t.comparison.table.design}</TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <span>{t.comparison.table.designMinimalist}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span>{t.comparison.table.designFocus}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span>{t.comparison.table.designFlexible}</span>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="space-y-1">
                                    <div>{t.comparison.table.designOpificio42Industrial}</div>
                                    <div>{t.comparison.table.designOpificio42Minimalist}</div>
                                    <div>{t.comparison.table.designOpificio42Light}</div>
                                    <div>{t.comparison.table.designOpificio42Solar}</div>
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                {/* Why Choose VareseLab */}
                <Card className="bg-primary-50 dark:bg-primary-900/50 p-2 border-1">
                    <CardHeader className="pb-2">
                        <h2 className="text-3xl font-bold text-primary-700 mx-auto">
                            {t.comparison.whyVareselab.title}
                        </h2>
                    </CardHeader>
                    <CardBody>

                        <div className="grid grid-cols-1 desktop:grid-cols-2 gap-6">
                            <PositivePoint
                                title={t.comparison.whyVareselab.transparentPricing.title}
                                description={t.comparison.whyVareselab.transparentPricing.description}
                            />
                            <PositivePoint
                                title={t.comparison.whyVareselab.authenticCommunity.title}
                                description={t.comparison.whyVareselab.authenticCommunity.description}
                            />
                            <PositivePoint
                                title={t.comparison.whyVareselab.totalFlexibility.title}
                                description={t.comparison.whyVareselab.totalFlexibility.description}
                            />
                            <PositivePoint
                                title={t.comparison.whyVareselab.productivityFocus.title}
                                description={t.comparison.whyVareselab.productivityFocus.description}
                            />
                        </div>
                    </CardBody>
                </Card>

                {/* CTA */}
                <div className="max-w-4xl mx-auto p-8 desktop:p-12 text-center">
                    <h2 className="text-2xl desktop:text-3xl font-bold mb-4">
                        {t.comparison.cta.title}
                    </h2>
                    <p className="text-lg mb-8 text-default-600">
                        {t.comparison.cta.description}
                    </p>
                    <div className="flex flex-col desktop:flex-row gap-4 justify-center items-center">
                        <CtaButton
                            as={Link}
                            href={getLocalizedPath('/', language)}
                            className="plausible-event-name=cta+confronto+home"
                        >
                            {t.comparison.cta.discoverVareselab}
                        </CtaButton>
                        <CtaButton
                            as={Link}
                            href={routes.preRegistration("it")}
                            variant="bordered"
                            className="plausible-event-name=cta+confronto+preregistrazione text-default-500"
                        >
                            {t.comparison.cta.preRegister}
                        </CtaButton>
                    </div>
                </div>

            </div>
        </>
    );
}

function PositivePoint({ title, description }: { title: string; description: string }) {
    return (
        <div className="flex items-start gap-3">
            <IconCheck className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
            <div>
                <h3 className="">{title}</h3>
                <p className="text-default-500">{description}</p>
            </div>
        </div>
    );
}